import { redirect } from '@tanstack/react-router';

import { getTokenClaims } from '@shared/auth/token.store';

/**
 * Role-code gate for modules whose access rule is a role, not a per-action
 * permission. Unlike `requirePermission`, this reads the token's real `roles`
 * claim and DENIES when it is absent — that failing-closed is the whole point.
 */

/** Security is Director and Admin only. */
export const SECURITY_ROLES = ['ADMIN', 'DIRECTOR', 'SUPERADMIN'] as const;

/** True when the current access token carries any of `codes` (or SUPERADMIN). */
export function hasAnyRole(codes: readonly string[]): boolean {
  const roles = getTokenClaims()?.roles;
  if (!roles) return false;
  if (roles.includes('SUPERADMIN')) return true;
  return codes.some((code) => roles.includes(code));
}

/**
 * `beforeLoad` factory — redirects to `/unauthorized` unless the token carries
 * one of `codes`. The parent route has already refreshed the JWT by this point,
 * so a claimless token means no roles rather than a session still warming up.
 */
export function requireRole(codes: readonly string[]) {
  return (): void => {
    if (hasAnyRole(codes)) return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/unauthorized' });
  };
}
